import { gql } from 'graphql-tag'

export const POSTCODE_FIELDS = gql`
  fragment PostcodeFields on Postcode {
    _id
    code
    name
    region
    locality
    active
    featured
    subRegion
    congestion
  }
`

export const TERMINAL_READER_FIELDS = gql`
  fragment TerminalReaderFields on TerminalReader {
    id
    label
    deviceType
    serialNumber
    status
    ipAddress
    locationId
    staticLocationId
    lastSeenAt
  }
`

export const TERMINAL_TIP_SETTINGS_FIELDS = gql`
  fragment TerminalTipSettingsFields on TerminalTipSettings {
    percentages
  }
`

export const STATIC_LOCATION_FIELDS = gql`
  fragment SaasSettingsStaticLocationFields on SaasSettingsStaticLocation {
    _id
    enabled
    name
    summary
    externalAddressId
    hideFullAddress
    minBookingAmount
    minBookingDate
    defaultPackdownTime
    defaultAvailabilityRange
    timeSlotInterval
    autoAcceptBookingRequests
  }
`

export const SALON_MEMBERSHIP_FIELDS = gql`
  fragment SalonMembershipFields on SalonMember {
    _id
    salonId
    memberId
    color
    displayName
    picture
    title
    permissions
    colleagues {
      memberId
      displayName
      color
      picture
    }
    status
    createdAt
    updatedAt
    locations {
      ...SaasSettingsStaticLocationFields
    }
    businessName
    salonEmailAddress
    onlinePaymentsEnabled
  }
`

export const USER_FIELDS = gql`
  fragment UserFields on User {
    _id
    emailAddress
    firstName
    lastName
    professionalProfile {
      calendarColors {
        categories
      }
      bio
      favouriteCount
      reviewCount
      averageReview
      treatmentIds
      postcodeIds
      regions
      defaultTransportMode
      treatments {
        _id
        name
        type
      }
      postcodes {
        ...PostcodeFields
      }
      numberOfBookings
      notificationPreferences {
        privateBookingsTomorrowSummary
        privateBookingsTodaySummary
      }
    }
    stripeConnectedAccount
    acceptedTermsSaaS
    countryISOCode
    currencyCode
    timezone
  }
`

export const CHAT_FIELDS = gql`
  fragment ChatFields on Chat {
    _id
    professionalId
    professional {
      firstName
      lastName
    }
    clientId
    client {
      firstName
      lastName
    }
    lastMessage {
      message
      sentAt
    }
    messageCount {
      professional {
        unreadMessages
        sent
      }
      client {
        unreadMessages
        sent
      }
    }
  }
`

export const BOOKINGS_FIELDS = gql`
  fragment BookingsFields on Booking {
    _id
    status
    date
    address
    totalPrice
    totalDuration
    totalFee
    title
    note
    type
    requestedFavouriteIds
    rejectedByIds
    userId
    items
    clientNote
    clientPictures
    professionalId
    clientReview {
      rating
      comment
    }
    clientDetails {
      firstName
      gender
      contactNumber
      note
      accountOwner
    }
    postcode {
      ...PostcodeFields
    }
    adminFees {
      name
      amount
    }
    timezone
    requestedProfessionalResponses {
      professionalId
      status
      respondedAt
      availabilityAdded
    }
    crossSellPromoCode
    calendarId
  }
`

export const BOOKING_FIELDS = gql`
  fragment BookingFields on Booking {
    ...BookingsFields
    payoutDate
    adjustments {
      _id
      adjustmentType
      note
      amount
      payoutDate
      professionalId
    }
  }
`

export const SAAS_SETTINGS_FIELDS = gql`
  fragment SaasSettingsFields on SaasSettings {
    themeSettings {
      colorPrimary
      colorSecondary
      colorNeutralLight
      colorNeutralDark
      cornerStyle
      shadowStyle
      titlesFont
      titlesMotif
      bodyFont
    }
    bookNowUrl
    minBookingAmount
    minBookingDate
    businessName
    salonMemberOnly
    subtext
    defaultPackdownTime
    defaultTravelTime
    useTravelTime
    isPublic
    themePrimary
    themeBackground
    themeTextColor
    emailThemeBackground,
    emailThemeTextColor,
    emailThemePrimary,
    requireTermsAndConditions
    receiveClientEmails
    branding
    features {
      hours
    }
    lookBook {
      imageUrl
      caption
    }
    info {
      title
      body
    }
    mobileLocation {
      _id
      enabled
      fee
      summary
      travelDistanceMiles
      externalAddressId
      minBookingAmount
      minBookingDate
      defaultPackdownTime
      defaultTravelTime
      useTravelTime
      defaultAvailabilityRange
      timeSlotInterval
      autoAcceptBookingRequests
      requestParkingInformation
    }
    requestsBlockCalendar
    staticLocations {
      ...SaasSettingsStaticLocationFields
    }
    defaultPaymentMethod
    paymentMethods {
      label
      type
    }
    defaultCancellationText
    contactDetails {
      type
      value
    }
    linkDescription
    treatmentCategoriesOrder
    treatmentsDisplayName
    pinnedReviewId
    clientDepositSettings {
      clientType
      depositRequired
      depositAmount
      useFixedAmount
      disableApplePay
    }
    requireBirthday
    requireBirthdayExplaination
    rescheduleRequestsEnabled
    reschedulingTimeFrame
    seoSettings {
      metaTitle
      metaDescription
      pageH1
      pageH2
    }
    localBusinessVisibility {
      addressVisibility
      showPhoneNumber
    }
  }
`

export const SUBSCRIPTION_DETAILS_FIELDS = gql`
  fragment SubscriptionDetailsFields on SoloSubscriptionDetails {
    chargeBeeId
    saasBookingsThisPeriod {
      value
    }
    trialExtensions {
      date
      plan
    }
    saasReceipts {
      productId
      token
    }
    saasSubscriptions {
      plan
      status
      platform
      nextBilling
      entitlements {
        id
        value
      }
      planType
      couponIds
      totalDiscount
      inTrial
      trialEnd
      trialStart
    }
    entitlements {
      id
      value
      enabled
      updatedAt
      lastActiveAt
    }
    entitlementOverrides
    isUpdating
  }
`

export const PROFESSIONAL_FIELDS = gql`
  fragment ProfessionalFields on Professional {
    _id
    role
    emailAddress
    firstName
    lastName
    emailConfirmed
    guestKey
    mobile
    mobileConfirmed
    gender
    genderSelfDescription
    stripeConnectedAccount
    stripePayoutEnabled
    stripeAccountStatus
    stripePayoutInterval
    stripeLimits {
      tapToPay
      dailyTransactions
    }
    soloServiceFee
    acceptedTermsPA
    acceptedTermsSaaS
    lastHealthPPECheck
    healthPPEConfirmed
    profileUrl
    blocked
    createdAt
    referralCode
    professionalProfile {
      calendarColors {
        categories
      }
      bio
      favouriteCount
      reviewCount
      averageReview
      treatmentIds
      postcodeIds
      regions
      defaultTransportMode
      treatments {
        _id
        name
        type
      }
      postcodes {
        ...PostcodeFields
      }
      numberOfBookings
      information
      createdAt
      genderPreference
      saasProfileUrl
      saasSettings {
        ...SaasSettingsFields
      }
      notificationPreferences {
        privateBookingsTomorrowSummary
        privateBookingsTodaySummary
      }
      averageExternalReview
      externalReviewCount
      completedSetupSteps
      completedSetupTasks
      giftCardSettings {
        enabled
        daysBeforeExpires
      }
    }
    stripeCapabilities {
      card_payments
      transfers
    }
    subscriptionDetails {
      ...SubscriptionDetailsFields
    }
    attributionData
    soloReferralId
    countryISOCode
    currencyCode
    timezone
    googleBusinessAuth {
      refreshToken
      accessToken
      selectedLocationId
      selectedAccountId
      scopes
      placeId
      localBusinessSchemaData {
        name
      }
    }
    googleCalendarAuth {
      refreshToken
      accessToken
      calendarId
    }
  }
`

export const EXT_CLIENT_FIELDS = gql`
  fragment ExternalClientFields on ExternalClient {
    _id
    lastName
    firstName
    mobile
    emailAddress
    tags
    addresses
    notes
    color
    gender
    professionalId
    birthday
    blocked
    stats {
      acceptedOrCompletedBookings
      totalSpend
      averageSpend
      lastBookedDate
      noShows
    }
    notifications {
      emailCrm
      smsCrm
      emailUpdates
      smsUpdates
    }
    loyaltyProgress {
      loyaltySchemeId
      current
      totalRedeems
    }
    stripeCustomerId
    isRedacted
    deletedAt
  }
`

export const EMAIL_CAMPAIGN_FIELDS = gql`
  fragment EmailCampaignFields on EmailCampaign {
    _id
    label
    themeColors {
      themePrimary
      themeBackground
      themeTextColor
      contrastTextColor
    }
    scheduledFor
    customRecipientIds
    applyToAllClients
    recipientClientGroups
    type
    messageType
    lastSentAt
    createdAt
    emailSubject
    emailContent {
      _id
      blockType
      text
      url
      data {
        promocodeId
      }
    }
    status
    sentCount
    triggerType
  }
`

export const SMS_CAMPAIGN_FIELDS = gql`
  fragment SmsCampaignFields on SmsCampaign {
    _id
    label
    scheduledFor
    customRecipientIds
    applyToAllClients
    recipientClientGroups
    type
    messageType
    lastSentAt
    createdAt
    messageContent
    status
    sentCount
    triggerType
  }
`

export const EXT_TREATMENT_FIELDS = gql`
  fragment ExternalTreatmentFields on ExternalTreatment {
    _id
    name
    price
    category
    duration
    bufferTime
    showOnPortal
    description
    order
    professionalId
    locations {
      locationId
      enabled
      price
    }
    schedule {
      duration
      type
      description
    }
    memberOverrides {
      professionalId
      price
      duration
      canPerformTreatment
    }
  }
`

export const EXTERNAL_PROMOCODE_FIELDS = gql`
  fragment ExternalPromocodeFields on ExternalPromocode {
    _id
    code
    redeemedBy
    redeemableBy
    expiryDate
    active
    treatmentIds
    discountAmount
    discountType
    professionalId
    createdAt
    customRedeemableExternalClientIds
    maxRedemptions
    applyToAllTreatments
    applyToAllClients
    label
    externalClientId
  }
`

export const PRIVATE_PAYMENT_FIELDS = gql`
  fragment PrivatePaymentFields on StripePaymentDetails {
    sessionId
    amount
    type
    description
    paymentIntent
    updatedAt
    externalPaymentId
    amountRefunded
    stripeType
  }
`

export const EXT_BOOKING_FIELDS = gql`
  fragment ExternalBookingFields on ExternalBooking {
    _id
    professionalId
    date
    color
    title
    notes
    totalFee
    feeAtBookingTime
    serviceFeePercentage
    totalDuration
    address
    clientDetails
    timezone
    externalReviewId
    externalReview {
      ...ExternalReviewFields
    }
    externalClientId
    externalClient {
      ...ExternalClientFields
    }
    items {
      _id
      treatmentId
      name
      fee
      duration
      category
      schedule {
        duration
        type
        description
      }
    }
    payments {
      ...PrivatePaymentFields
    }
    type
    bufferTime
    status
    source
    activityLog {
      title
      date
      properties {
        amount
        paymentType
      }
    }
    newCustomer
    sourceName
    platformCommission
    depositPercentage
    paymentMethod
    cancellationText
    cancellationReason
    consultationFormStatus
    consultationFormSectionIds
    businessName
    paymentStatus
    promocodeId
    promocodeDiscount {
      amount
      type
      code
    }
    repeats
    termsAndConditionsAccepted
    calendarId
    depositRequired
    useFixedAmount
    updatedAt
    createdAt
    originalBookingId
    rescheduleBookingId
    rescheduleType
    rescheduleReason
    emailUpdatesEnabled
    isProfessionalAddress
    allocatedProfessionalId
  }
`

export const EXT_ADDRESS_FIELDS = gql`
  fragment ExternalAddressFields on ExternalAddress {
    _id
    professionalId
    placeId
    line1
    line2
    region
    postalCode
    location {
      type
      coordinates
    }
    timezone
    staticLocation {
      ...SaasSettingsStaticLocationFields
    }
  }
`

export const EXTERNAL_REVIEW_FIELDS = gql`
  fragment ExternalReviewFields on ExternalReview {
    _id
    professionalId
    externalBookingId
    secretSpaReviewId
    externalClientName
    rating
    publicComment
    privateComment
    visible
    createdAt
    googleReviewId
    googleReviewReplyComment
    googleReviewReplyUpdateTime
  }
`

export const IN_APP_MESSAGE_FIELDS = gql`
  fragment InAppMessageFields on InAppMessage {
    _id
    name
    platform
    eventName
    match
    contentType
    updatedAt
  }
`

export const CONSULTATION_FORM_FIELDS = gql`
  fragment ConsultationFormFields on ConsultationForm {
    _id
    name
    professionalId
    treatmentIds
    applyToAllTreatments
  }
`

export const CONSULTATION_FORM_QUESTION_FIELDS = gql`
  fragment ConsultationFormQuestionFields on ConsultationFormQuestion {
    _id
    question
    type
    options
    required
    placeholder
    helpText
    templateName
    autofillId
  }
`

export const AUTOMATED_CAMPAIGN_FIELDS = gql`
  fragment AutomatedCampaignFields on AutomatedCampaign {
    campaignId
    triggerType
    daysOffset
    active
    sendAtHour
    messageType
    sendLog {
      date
      sentCount
      errors
    }
  }
`

export const SCHEDULED_CAMPAIGN_FIELDS = gql`
  fragment ScheduledCampaignFields on ScheduledCampaign {
    campaignId
    messageType
    scheduledFor
    externalClientIds
    status
  }
`

export const BUTTON_FIELDS = gql`
  fragment ButtonFields on ContentBlockButton {
    label
    url
    linkType
    showButton
  }
`

export const WEBPAGE_FIELDS = gql`
  fragment WebPageFields on WebPage {
    _id
    title
    path
    group
    order
    headerBlock {
      _id
      type
      color
      content {
       ... on ImageBlockContent {
        type
        title
        titleSize
        text
        layout
        shape
        textPosition
        textColor
        borderStyle
        imageUrl
        showReviews
        button {
          ...ButtonFields
        }
        secondaryButton {
          ...ButtonFields
        }
        isPageHeader
        imageBorderWidth
        data {
          breadcrumbs {
            name
            path
          }
        }
       }
      }
    }
    seoSettings {
      metaTitle
      metaDescription
    }
    showPageNavigation
    actionButton {
      content {
        ...ButtonFields
      }
    }
    visibility
    blocks {
      _id
      type
      color
      content {
        ... on TextBlockContent {
          text
          title
          type
          button {
            ...ButtonFields
          }
        }
        ... on ImageBlockContent {
          layout
          imageUrl
          alt
          title
          titleSize
          text
          type
          filter
          shape
          textPosition
          textColor
          borderStyle
          showReviews
          button {
            ...ButtonFields
          }
          secondaryButton {
          ...ButtonFields
          }
          isPageHeader
          imageBorderWidth
          data {
            breadcrumbs {
              name
              path
            }
          }
        }
        ... on LinkListBlockContent {
          items {
            _id
            iconName
            label
            subtext
            url
            linkType
          }
          button {
            ...ButtonFields
          }
          title
          type
        }
        ... on LogoListBlockContent {
          layout
          logos
          title
          type
        }
        ... on InfoBlockContent {
          items {
            _id
            heading
            text
          }
          button {
            ...ButtonFields
          }
          title
          type
        }
        ... on ImageCollectionBlockContent {
          layout
          items {
            _id
            alt
            imageUrl
          }
          title
          type
          button {
            ...ButtonFields
          }
        }
        ... on TreatmentListBlockContent {
          items {
            _id
          }
          data {
            items {
              _id
              name
              description
              price
              category
            }
          }
          treatmentsListDisplayType
          title
          type
          groupedCategories
          button {
            ...ButtonFields
          }
        }
        ... on LookbookBlockContent {
          items {
            alt
            imageUrl
          }
          title
          type
        }
        ... on ButtonBlockContent {
          label
          url
          title
          type
        }
        ... on ReviewsBlockContent {
        type
        title
        data {
          reviews {
            _id
            rating
            publicComment
            privateComment
            visible
            externalClientName
          }
        }
        layout
        reviewIds
        reviewsDisplayType
        button {
          ...ButtonFields
          }
        }
        ... on LocationBlockContent {
          type
          title
          text
          hideFullAddress
          locationId
          syncWithGoogle
          data {
            placeId
            staticLocationLat
            staticLocationLng
            localBusinessData {
              address {
                street
                city
                postalCode
                country
              }
            }
          }
        }
        ... on SalonMemberBlockContent {
          type
          title
          layout
          data {
            members {
              _id
              memberDisplayName
              picture
              title
            }
          }
        }
      }
    }
  }
`

export const LOYALTY_SCHEME_FIELDS = gql`
  fragment LoyaltySchemeFields on LoyaltyScheme {
    _id
    professionalId
    status
    startDate
    goal
    goalNumber
    minBookingFee
    applyToAllClients
    externalClientIds
    createdAt
    updatedAt
    promoCodeSettings {
      discountAmount
      discountType
      validityDays
      applyToAllTreatments
      treatmentIds
    }
    referralCodeSettings {
      discountAmount
      discountType
      validityDays
      applyToAllTreatments
      treatmentIds
    }
    schemeUsage
  }
`

export const EXTERNAL_GIFT_CARD_FIELDS = gql`
  fragment ExternalGiftCardFields on ExternalGiftCard {
    _id
    code
    amount
    expirationDate
    receiverEmail
    message
    senderEmail
    status
    balance
    transactions {
      date
      amount
      type
      externalBookingId
    }
  }
`

export const BUSINESS_PROFILE_DATA_FIELDS = gql`
  fragment BusinessProfileDataFields on BusinessProfileData {
    _id
    reviewsWithTextCount
    reviewsWithin60DaysCount
    averageRating
    reviewResponseRate
    photosCount
    clientPhotosCount
    photosWithin60DaysCount
    postsCount
    lastPostDate
    hasDescription
    hasAddress
    hasWebsite
    isVerified
    hasOpeningHours
    servicesCount
    hasPrimaryCategory
    createdAt
    professionalId
    updatedAt
    fetchedAt
  }
`
